import React, { useEffect, useState } from 'react';
import { App, Form, Input, Modal } from 'antd';
import { Requirement, RequirementConfirm, RequirementConfirmTypeEnum, RequirementItemDelEnum } from '@/types/requirement';
import apis from '@/apis/requirement';
import { useTranslation } from 'react-i18next';
import { RequirementContentWrap } from './style';
import usePassed from '@/pages/Sales/hooks/usePassed';
import useDataUpdate from '@/hooks/useDataUpdate';
import orderApis, { UpdateByKey } from "@/apis/order";
import { SaleOrderAdd, SaleOrderItemState } from '@/types/sales';
import { PageId } from '@/types/page';
import useRequirementParams from '../../hooks/useRequirementParams';
import { getRequirementConfirmItem, getQuotationRequestTruePrice } from '../../util';
import { divided, minus, multi, plus } from '@/utils/calc';
import RequirementReview from '../RequirementReview';


interface RequirementStepsProps {
  id: string;
  modalProps?: Omit<React.ComponentProps<typeof Modal>, 'open'>; // 可选的 Modal 配置项（除 open 外）
  visible: boolean; // 控制 Modal 是否可见
  onClose: () => void; // 关闭 Modal 的回调
  onOk: (value?: string) => void; // 关闭 Modal 的回调
  order?: SaleOrderItemState | null;
  confirms: Array<RequirementConfirm>;
  detail?: Requirement;
  requirePassCb?: (data: string[]) => void;
  newConfirms?: Array<RequirementConfirm>
}

const RequirementSteps: React.FC<RequirementStepsProps> = ({
  id,
  modalProps,
  visible,
  onClose,
  onOk,
  order,
  confirms,
  detail,
  requirePassCb,
  newConfirms
}) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();
  const { pageId, params } = useRequirementParams(true);
  const { update } = useDataUpdate<[string, UpdateByKey]>({ api: orderApis.updateByKey });
  const [desc, setDesc] = useState<string>('')
  const [conLoading, setConLoading] = useState<boolean>(false)

  const { passed, loading } = usePassed({
    setId: () => { },
    updateApi: update,
    handleOk: () => { },
    errorTip: t('sale.passError')
  })

  useEffect(() => {
    if (!visible) {
      setDesc('')
    }
  }, [visible])

  const confirmPass = async (req: Requirement) => {
    if (detail && detail.saleOrder) {  //物控手动添加的没有销售单，不用update
      let bomCost = 0;
      req.items.forEach(quo => {
        if (quo.equotationRequest) {
          bomCost += getQuotationRequestTruePrice(quo.equotationRequest)
        }
      })
      let netProfit = plus(minus(
        minus(detail.saleOrder.totalRealAmount, bomCost + ''),
        divided(multi(detail.saleOrder.commissionRate || '', detail.saleOrder.totalRealAmount), '100')
      ), detail.saleOrder.extPrice + '')
      let saleOrderInfo: SaleOrderAdd = {
        ...detail.saleOrder,
        shippingMethod: detail.saleOrder.shippingMethod || '',
        otherNumber1: detail.saleOrder.otherNumber1 || '',
        discount: detail.saleOrder.discount || '',
        mfgr: detail.saleOrder?.mfgr || '',
        customerModel: detail.saleOrder?.customerModel || '',
        urgencyLevel: detail.saleOrder?.urgencyLevel || '',
        specialReq: detail.saleOrder?.specialReq || '',
        bomCost: bomCost + '',      //TODO转汇率
        netProfit,
        items: [],
        // netProfit: (
        //   Number(detail.saleItem.totalRealAmount) - bomCost - (
        //     Number(detail.saleItem.order.commissionRate || '') * Number(detail.saleItem.totalRealAmount) / 100
        //   ) + Number(detail.saleItem.order.extPrice || '')) + '',
      }
      await orderApis.update(order?.id ?? '', saleOrderInfo)
    }
    params && params.confirmType && apis.confirms(id,
      newConfirms ? newConfirms.map(con => ({ ...con, desc: con.type === RequirementConfirmTypeEnum.MATERIAL_CONTROL ? desc : con.desc })) : getRequirementConfirmItem(confirms, params.confirmType, "next", desc)
    ).then(() => onOk()).finally(() => setConLoading(false))
  }

  const handleOk = async () => {
    if (id && params && params.confirmType) {
      setConLoading(true)
      if (pageId === PageId.SALE_NOT_PASS && order) {
        let error = ''

        if (!order?.innerNumber) {
          error = t("sale.innerNumberPre")
        }
        if (error) {
          notification.error({ placement: 'top', message: t('sale.passError'), description: error })
          setConLoading(false)
          return;
        }
        apis.confirms(id,
          newConfirms ? newConfirms.map(con => ({ ...con, desc: con.type === RequirementConfirmTypeEnum.MATERIAL_CONTROL ? desc : con.desc })) : getRequirementConfirmItem(confirms, params.confirmType, "next", desc)
        )
          .then(() => {
            return passed(order)?.then(() => onOk())
          }).finally(() => setConLoading(false))
      } else {
        if (pageId === PageId.PUR_NOT_PASS) {
          detail && confirmPass(detail)
        } else apis.confirms(id,
          newConfirms ? newConfirms.map(con => ({ ...con, desc: con.type === RequirementConfirmTypeEnum.MATERIAL_CONTROL ? desc : con.desc })) : getRequirementConfirmItem(confirms, params.confirmType, "next", desc)
        ).then(() => onOk()).finally(() => setConLoading(false))
      }
    } else {
      onOk(desc)
    }
  }

  return (
    <Modal
      open={visible} // 控制 Modal 显示状态
      onCancel={onClose} // 点击关闭时触发
      onOk={handleOk}
      title={t('requirement.requirementAppInfo')}
      confirmLoading={loading || conLoading}
      {...modalProps} // 其他 Modal 配置项
    >
      <RequirementContentWrap>
        {
          newConfirms && <>
            <div style={{ marginBottom: 12, fontWeight: "bolder" }}>{t('requirement.approveSaveTip')}</div>
            <Form.Item label={t('requirement.oldApprove')} layout="vertical" style={{ width: "100%", marginBottom: 6 }}>
              <RequirementReview confirms={detail?.confirms ?? []} isSHowLine />
            </Form.Item>
            <Form.Item label={t('requirement.newApprove')} layout="vertical" style={{ width: "100%", marginBottom: 6 }}>
              <RequirementReview confirms={newConfirms ?? []} isSHowLine />
            </Form.Item>
          </>
        }
        <Form.Item label={t('requirement.approveRemark')} layout="vertical" style={{ width: "100%", marginBottom: 6 }}>
          <Input.TextArea
            placeholder={t('requirement.approvalTip')}
            maxLength={100}
            showCount
            autoSize={{ minRows: 6, maxRows: 8 }}
            value={desc}
            onChange={e => setDesc(e.target.value)}
          />
        </Form.Item>
      </RequirementContentWrap>
    </Modal >
  );
};

export default RequirementSteps;
